export type ShortcutContext = "viewer" | "admin" | "global";

export type ShortcutDefinition = {
    key: string;
    shift?: boolean;
    handler: string;
    description: string;
    section: string;
    context: ShortcutContext;
};

export const ADMIN_TAB_SHORTCUTS: Record<string, string> = {
    "1": "processing",
    "2": "review",
    "3": "library",
    "4": "tags",
    "5": "ai-analysis",
};

export const SHORTCUT_DEFINITIONS: ShortcutDefinition[] = [
    // viewer navigation
    {
        key: "Space",
        handler: "onNext",
        description: "Next item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "n",
        handler: "onNext",
        description: "Next item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "ArrowRight",
        handler: "onNext",
        description: "Next item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "p",
        handler: "onPrevious",
        description: "Previous item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "Backspace",
        handler: "onPrevious",
        description: "Previous item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "ArrowLeft",
        handler: "onPrevious",
        description: "Previous item",
        section: "Navigation",
        context: "viewer",
    },
    {
        key: "f",
        handler: "onToggleFullscreen",
        description: "Toggle fullscreen",
        section: "Navigation",
        context: "viewer",
    },

    // viewer voting
    {
        key: "v",
        handler: "onVote",
        description: "Vote for current item",
        section: "Voting",
        context: "viewer",
    },

    // viewer tagging
    {
        key: "t",
        handler: "onFocusTagInput",
        description: "Focus tag input",
        section: "Tagging",
        context: "viewer",
    },

    // viewer display
    {
        key: "o",
        handler: "onToggleOcrOverlay",
        description: "Toggle OCR text overlay",
        section: "Display",
        context: "viewer",
    },
    {
        key: "i",
        handler: "onToggleAiAnalysis",
        description: "Toggle AI analysis",
        section: "Display",
        context: "viewer",
    },
    {
        key: "c",
        handler: "onCrop",
        description: "Crop image (admin)",
        section: "Display",
        context: "viewer",
    },
    {
        key: "d",
        handler: "onDownload",
        description: "Download current item",
        section: "Display",
        context: "viewer",
    },
    {
        key: "e",
        handler: "onEdit",
        description: "Edit tags (focus input)",
        section: "Display",
        context: "viewer",
    },

    // viewer actions
    {
        key: "s",
        handler: "onShare",
        description: "Create share link",
        section: "Actions",
        context: "viewer",
    },

    // viewer layout
    {
        key: "[",
        handler: "onToggleLeftSidebar",
        description: "Toggle left sidebar",
        section: "Layout",
        context: "viewer",
    },
    {
        key: "]",
        handler: "onToggleRightSidebar",
        description: "Toggle right sidebar",
        section: "Layout",
        context: "viewer",
    },
    {
        key: "h",
        handler: "onToggleFilmstrip",
        description: "Toggle history filmstrip",
        section: "Layout",
        context: "viewer",
    },

    // admin navigation
    {
        key: "j",
        handler: "onFocusNext",
        description: "Next item",
        section: "Navigation",
        context: "admin",
    },
    {
        key: "ArrowDown",
        handler: "onFocusNext",
        description: "Next item",
        section: "Navigation",
        context: "admin",
    },
    {
        key: "k",
        handler: "onFocusPrevious",
        description: "Previous item",
        section: "Navigation",
        context: "admin",
    },
    {
        key: "ArrowUp",
        handler: "onFocusPrevious",
        description: "Previous item",
        section: "Navigation",
        context: "admin",
    },
    {
        key: "Enter",
        handler: "onOpenFocused",
        description: "Open focused item",
        section: "Navigation",
        context: "admin",
    },

    // admin actions
    {
        key: "a",
        handler: "onApproveFocused",
        description: "Approve focused item",
        section: "Actions",
        context: "admin",
    },
    {
        key: "x",
        handler: "onRejectFocused",
        description: "Reject focused item",
        section: "Actions",
        context: "admin",
    },

    // global
    {
        key: "?",
        shift: true,
        handler: "onShowHelp",
        description: "Show keyboard shortcuts",
        section: "General",
        context: "global",
    },
    {
        key: "Escape",
        handler: "onEscape",
        description: "Clear focus / Close modal",
        section: "General",
        context: "global",
    },
];

type ShortcutSectionDisplay = {
    title: string;
    shortcuts: { keys: string[]; description: string; }[];
};

export const getShortcutSections = (
    context: ShortcutContext,
): ShortcutSectionDisplay[] => {
    const relevant = SHORTCUT_DEFINITIONS.filter(
        (def) => def.context === context || def.context === "global",
    );

    const sectionMap = new Map<
        string,
        { keys: string[]; description: string; }[]
    >();

    for (const def of relevant) {
        if (!sectionMap.has(def.section)) {
            sectionMap.set(def.section, []);
        }
        const section = sectionMap.get(def.section)!;
        const existing = section.find((entry) =>
            entry.description === def.description
        );
        const displayKey = def.shift
            ? `Shift+${def.key}`
            : def.key === " "
            ? "Space"
            : def.key;
        if (existing) {
            existing.keys.push(displayKey);
        } else {
            section.push({
                keys: [displayKey],
                description: def.description,
            });
        }
    }

    if (context === "admin") {
        const navSection = sectionMap.get("Navigation") ?? [];
        navSection.unshift({
            keys: ["1-5"],
            description: "Switch tab by number",
        });
        sectionMap.set("Navigation", navSection);
    }

    return Array.from(sectionMap.entries()).map(([title, shortcuts]) => ({
        title,
        shortcuts,
    }));
};
